/**
 * How the skill list is organised (Skills UX pass 2026-09-06). A skill list
 * is read for "which skills am I building, which one is ready for my practice
 * next, and how far along is each", so the default grouping is by movement:
 *
 *   Ready to check        an open checkpoint, a finished path, or the stage says so — answer it
 *   In motion             a next step is set, or it was practised in the last fourteen days
 *   Needs a first step    live, nothing practised, no path yet — the next move is to start
 *   Waiting for you       has history or a path, but nothing next and nothing recent
 *   Paused                paused by the person (parked-by-rationing skills stay in their movement section)
 *   Good enough for now   "sufficient for the goal" — resting with honour, collapsed
 *
 * Three status axes reduce to one line of words here (`skillState`), with a
 * fixed precedence: Paused › Parked › Good enough for now › Ready to check ›
 * nothing. Parked is rationing's word, not the person's choice: it shows as a
 * tag but the skill stays live — it sits in its movement section and can be
 * mapped and edited (owner 2026-09-06, skill 240). The level pair is not a
 * status; it is the ladder.
 *
 * Pure functions with no runtime imports so node can probe them
 * (`npm run test:skills`); the page injects the life-area labeller.
 */
export type SkillSectionKey = 'check' | 'moving' | 'start' | 'waiting' | 'paused' | 'enough'
export type SkillGroupBy = 'movement' | 'goal' | 'area' | 'none'
export type SkillSortBy = 'attention' | 'level' | 'created' | 'updated'
export type SortDir = 'asc' | 'desc'
export interface SortSpec { by: SkillSortBy; dir: SortDir }

/** The fields the rules read; the page's Skill type satisfies it. */
export interface SkillLike {
  id: number | string
  title: string
  status?: string | null
  skill_status?: string | null
  skill_status_changed_at?: string | null
  current_level?: string | null
  target_level?: string | null
  target_date?: string | null
  created_at?: string | null
  updated_at?: string | null
  life_area?: string | null
  parent?: { type: string; id: number; title: string | null; life_area?: string | null } | null
}

export interface SkillCardLike {
  path?: { status: string; done: number; total: number; current?: unknown } | null
  next?: { date?: string | null } | null
  today?: unknown | null
  days_active?: number
  window_days?: number
  window_capped?: boolean
  days_total?: number
  last_date?: string | null
  checkpoint?: unknown | null
}

export interface SkillGroup<S extends SkillLike = SkillLike> {
  key: string
  title: string
  quiet?: string
  items: S[]
  collapsedByDefault?: boolean
}

export const SECTION_TITLE: Record<SkillSectionKey, string> = {
  check: 'Ready to check',
  moving: 'In motion',
  start: 'Needs a first step',
  waiting: 'Waiting for you',
  paused: 'Paused',
  enough: 'Good enough for now',
}
const SECTION_ORDER: SkillSectionKey[] = ['check', 'moving', 'start', 'waiting', 'paused', 'enough']

// ── the ladder ──

export const LEVELS = ['novice', 'beginner', 'developing', 'proficient', 'advanced'] as const
export type Level = (typeof LEVELS)[number]
const LEVEL_ALIAS: Record<string, Level> = { intermediate: 'developing', expert: 'advanced' }
export const LEVEL_WORD: Record<Level, string> = { novice: 'Novice', beginner: 'Beginner', developing: 'Developing', proficient: 'Proficient', advanced: 'Advanced' }

/** Any stored level word → a canonical rung, or null when nothing is asserted. */
export function normalizeLevel(v: string | null | undefined): Level | null {
  const k = String(v || '').trim().toLowerCase()
  if ((LEVELS as readonly string[]).includes(k)) return k as Level
  return LEVEL_ALIAS[k] || null
}
export function levelIndex(v: string | null | undefined): number | null {
  const l = normalizeLevel(v)
  return l ? LEVELS.indexOf(l) : null
}
export function levelWord(v: string | null | undefined): string | null {
  const l = normalizeLevel(v)
  return l ? LEVEL_WORD[l] : null
}

// ── the three axes, one line of words ──

export type SkillStateKey = 'paused' | 'parked' | 'enough' | 'check' | 'active'
export interface SkillState { key: SkillStateKey; words: string | null }

export function skillState(s: SkillLike): SkillState {
  const lane = String(s.status || '').toLowerCase()
  const stage = String(s.skill_status || '').toLowerCase()
  if (stage === 'paused' || lane === 'paused_by_user' || lane === 'hold') return { key: 'paused', words: 'Paused' }
  if (lane === 'parked') return { key: 'parked', words: 'Parked' }
  if (stage === 'sufficient_for_goal') return { key: 'enough', words: 'Good enough for now' }
  if (stage === 'ready_for_review') return { key: 'check', words: 'Ready to check' }
  return { key: 'active', words: null }
}

export const isResting = (s: SkillLike): boolean => skillState(s).key === 'paused'
export const isEnough = (s: SkillLike): boolean => skillState(s).key === 'enough'

/** Whole local days from one YYYY-MM-DD to another; null when unreadable. */
export function daysBetween(fromKey: string, toKey: string): number | null {
  const a = /^(\d{4})-(\d{2})-(\d{2})/.exec(fromKey), b = /^(\d{4})-(\d{2})-(\d{2})/.exec(toKey)
  if (!a || !b) return null
  const da = new Date(Number(a[1]), Number(a[2]) - 1, Number(a[3]))
  const db = new Date(Number(b[1]), Number(b[2]) - 1, Number(b[3]))
  return Math.round((db.getTime() - da.getTime()) / 86_400_000)
}

/** True when the "get there by" date is behind us (local calendar days). */
export function targetPassed(s: SkillLike, todayKey: string): boolean {
  if (!s.target_date) return false
  const d = daysBetween(todayKey, s.target_date)
  return d != null && d < 0
}

export function sectionOf(s: SkillLike, card: SkillCardLike | null | undefined, todayKey: string): SkillSectionKey {
  const state = skillState(s)
  if (state.key === 'enough') return 'enough'
  if (state.key === 'paused') return 'paused'
  if (card?.checkpoint || card?.path?.status === 'completed' || state.key === 'check') return 'check'
  if (card?.today || card?.next) return 'moving'
  if (card?.last_date) {
    const d = daysBetween(card.last_date, todayKey)
    if (d != null && d <= 14) return 'moving'
  }
  if (!(card?.days_total || 0) && !card?.path) return 'start'
  return 'waiting'
}

const cmp = (a: string | number, b: string | number) => (a < b ? -1 : a > b ? 1 : 0)
const title = (s: SkillLike) => s.title.toLowerCase()

export function sortSection<S extends SkillLike>(items: S[], key: SkillSectionKey, cards: Record<string, SkillCardLike | undefined>): S[] {
  const card = (s: S) => cards[String(s.id)]
  const arr = [...items]
  switch (key) {
    case 'moving':
      // practised today first, then the soonest next step, then the most recently practised
      return arr.sort((a, b) =>
        cmp(card(a)?.today ? 0 : 1, card(b)?.today ? 0 : 1)
        || cmp(String(card(a)?.next?.date || '9999'), String(card(b)?.next?.date || '9999'))
        || cmp(String(card(b)?.last_date || ''), String(card(a)?.last_date || ''))
        || cmp(title(a), title(b)))
    case 'waiting':
      return arr.sort((a, b) => cmp(String(card(b)?.last_date || ''), String(card(a)?.last_date || '')) || cmp(title(a), title(b)))
    case 'start':
      return arr.sort((a, b) => cmp(String(b.created_at || ''), String(a.created_at || '')))
    case 'enough':
      return arr.sort((a, b) => cmp(String(b.skill_status_changed_at || b.updated_at || ''), String(a.skill_status_changed_at || a.updated_at || '')))
    default:
      return arr.sort((a, b) => cmp(title(a), title(b)))
  }
}

export function timeKey(s: SkillLike, by: 'created' | 'updated'): string {
  return String((by === 'created' ? (s.created_at || s.updated_at) : (s.updated_at || s.created_at)) || '')
}

export function sortByTime<S extends SkillLike>(items: S[], by: 'created' | 'updated', dir: SortDir): S[] {
  return [...items].sort((a, b) => {
    const ka = timeKey(a, by), kb = timeKey(b, by)
    if (!ka || !kb) return cmp(ka ? 0 : 1, kb ? 0 : 1)
    return dir === 'desc' ? cmp(kb, ka) : cmp(ka, kb)
  })
}

/** By where they are on the ladder; a skill with no level asserted goes last either way. */
export function sortByLevel<S extends SkillLike>(items: S[], dir: SortDir): S[] {
  return [...items].sort((a, b) => {
    const ia = levelIndex(a.current_level), ib = levelIndex(b.current_level)
    if (ia == null || ib == null) return cmp(ia == null ? 1 : 0, ib == null ? 1 : 0)
    return (dir === 'desc' ? cmp(ib, ia) : cmp(ia, ib)) || cmp(title(a), title(b))
  })
}

export function matchesQuery(s: SkillLike & { description?: string | null; life_areas?: string[] | null; practice_cadence?: string | null }, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  const hay = [s.title, s.description, s.parent?.title, s.life_area, ...(s.life_areas || []), levelWord(s.current_level), levelWord(s.target_level)].filter(Boolean).join(' ').toLowerCase()
  return q.split(/\s+/).every((w) => hay.includes(w))
}

export interface SkillSummary { total: number; moving: number; check: number; practisedWeek: number; resting: number; enough: number }

export function summarize(skills: SkillLike[], cards: Record<string, SkillCardLike | undefined>, todayKey: string): SkillSummary {
  const s: SkillSummary = { total: 0, moving: 0, check: 0, practisedWeek: 0, resting: 0, enough: 0 }
  for (const sk of skills) {
    s.total += 1
    const k = sectionOf(sk, cards[String(sk.id)], todayKey)
    if (k === 'moving') s.moving += 1
    if (k === 'check') s.check += 1
    if (k === 'paused') s.resting += 1
    if (k === 'enough') s.enough += 1
    const last = cards[String(sk.id)]?.last_date
    if (last) { const d = daysBetween(last, todayKey); if (d != null && d >= 0 && d <= 6) s.practisedWeek += 1 }
  }
  return s
}

export function summaryWords(s: SkillSummary): string {
  if (s.total === 0) return 'Nothing here yet'
  const parts = [`${s.total} skill${s.total === 1 ? '' : 's'}`]
  if (s.check) parts.push(`${s.check} ready to check`)
  if (s.moving) parts.push(`${s.moving} in motion`)
  if (s.practisedWeek) parts.push(`${s.practisedWeek} practised this week`)
  else if (s.total > s.resting + s.enough) parts.push('nothing practised this week')
  if (s.resting) parts.push(`${s.resting} paused`)
  if (s.enough) parts.push(`${s.enough} good enough for now`)
  return parts.join(' · ')
}

const attentionOrder = <S extends SkillLike>(cards: Record<string, SkillCardLike | undefined>, todayKey: string) => (a: S, b: S) => {
  const ka = sectionOf(a, cards[String(a.id)], todayKey), kb = sectionOf(b, cards[String(b.id)], todayKey)
  if (ka !== kb) return cmp(SECTION_ORDER.indexOf(ka), SECTION_ORDER.indexOf(kb))
  return sortSection([a, b], ka, cards)[0] === a ? -1 : 1
}

export function groupSkills<S extends SkillLike>(
  skills: S[], cards: Record<string, SkillCardLike | undefined>, by: SkillGroupBy, todayKey: string,
  labelArea: (value: string) => string = (v) => v, sort: SortSpec = { by: 'attention', dir: 'desc' },
): SkillGroup<S>[] {
  const groups = buildGroups(skills, cards, by, todayKey, labelArea)
  if (sort.by === 'attention') return groups
  if (sort.by === 'level') return groups.map((g) => ({ ...g, items: sortByLevel(g.items, sort.dir) }))
  return groups.map((g) => ({ ...g, items: sortByTime(g.items, sort.by as 'created' | 'updated', sort.dir) }))
}

function buildGroups<S extends SkillLike>(skills: S[], cards: Record<string, SkillCardLike | undefined>, by: SkillGroupBy, todayKey: string, labelArea: (v: string) => string): SkillGroup<S>[] {
  if (by === 'movement') {
    const buckets: Record<SkillSectionKey, S[]> = { check: [], moving: [], start: [], waiting: [], paused: [], enough: [] }
    for (const s of skills) buckets[sectionOf(s, cards[String(s.id)], todayKey)].push(s)
    const anyLive = (['check', 'start', 'waiting'] as SkillSectionKey[]).some((k) => buckets[k].length > 0)
    return SECTION_ORDER
      .map((k): SkillGroup<S> => ({
        key: k, title: SECTION_TITLE[k], items: sortSection(buckets[k], k, cards),
        quiet: k === 'moving' && buckets.moving.length === 0 && anyLive ? 'Nothing in motion right now. Tap the mark on one you practised, or map a path.' : undefined,
        collapsedByDefault: k === 'enough',
      }))
      .filter((g) => g.items.length > 0 || g.quiet)
  }
  const live = skills.filter((s) => !isEnough(s))
  const enough = sortSection(skills.filter(isEnough), 'enough', cards)
  if (by === 'none') {
    const groups: SkillGroup<S>[] = []
    if (live.length) groups.push({ key: 'all', title: 'Skills', items: [...live].sort(attentionOrder(cards, todayKey)) })
    if (enough.length) groups.push({ key: 'enough', title: SECTION_TITLE.enough, items: enough, collapsedByDefault: true })
    return groups
  }
  const map = new Map<string, SkillGroup<S>>()
  const orphanKey = by === 'goal' ? 'none' : 'area:none'
  for (const s of live) {
    let key = orphanKey
    let t = by === 'goal' ? 'On their own' : 'No life area'
    if (by === 'goal' && s.parent?.title) { key = `${s.parent.type}:${s.parent.id}`; t = s.parent.title }
    if (by === 'area') {
      const area = s.life_area || s.parent?.life_area || null
      if (area) { key = `area:${area}`; t = labelArea(area) }
    }
    if (!map.has(key)) map.set(key, { key, title: t, items: [] })
    map.get(key)!.items.push(s)
  }
  const groups = [...map.values()]
  for (const g of groups) g.items.sort(attentionOrder(cards, todayKey))
  groups.sort((a, b) => cmp(a.key === orphanKey ? 1 : 0, b.key === orphanKey ? 1 : 0) || cmp(a.title.toLowerCase(), b.title.toLowerCase()))
  if (enough.length) groups.push({ key: 'enough', title: SECTION_TITLE.enough, items: enough, collapsedByDefault: true })
  return groups
}
